import { useEffect, useMemo, useState } from 'react';
import { sortListItems } from '../model/sort';
import type { IListItem } from '../model/listItem';
import type { IPageFilters } from '../pageFilters';
import { useAlertFilter } from './useAlertFilter';

/**
 * The Daily List's alert drill-down — the page opened from the dashboard's
 * Activity Alerts card (`?alertType=&alertDay=`).
 *
 * While it is on, the list is EXACTLY the alert's members (spec
 * 2026-07-20-unified-alerts-increment-design.md §7), resolved against the
 * whole feed (`allItems`) rather than the page's date window and saved filters:
 * the card counted the alert's members, not the members that happen to fall
 * in this week or survive the saved Status checkboxes (a "completed" alert
 * lists done activities, which the default filters hide). `rows` is the list
 * to render and to count.
 *
 * The segment reads the alert's kind for this visit (`types`) without writing
 * the saved preference — a saved "Issues" segment would otherwise empty every
 * activity alert. The saved filters are the view the chip returns to: changing
 * any of them (segment, panel, search) is leaving the drill-down, so the alert
 * descriptor is dropped and the change applies to the ordinary list.
 */
export function useAlertDrillDown(siteId: string | undefined, allItems: IListItem[], filters: IPageFilters) {
  const alert = useAlertFilter(siteId);

  // The saved filters as they stood when the drill-down began. A different
  // object later means the user changed one (the filters reducer mints a new
  // object only on a dispatch). Adjusted during render, like the windowing's
  // reset, so the first frame already knows.
  const [landedOn, setLandedOn] = useState<IPageFilters | null>(null);
  if (alert.isActive !== (landedOn !== null)) setLandedOn(alert.isActive ? filters : null);
  const filtersChanged = landedOn !== null && landedOn !== filters;
  const active = alert.isActive && !filtersChanged;

  const { clear } = alert;
  useEffect(() => {
    if (filtersChanged) clear();
  }, [filtersChanged, clear]);

  const { memberKeys } = alert;
  const rows = useMemo(
    () => (active ? sortListItems(allItems.filter((item) => memberKeys?.has(item.key))) : null),
    [active, allItems, memberKeys],
  );

  return {
    isActive: active,
    type: alert.type,
    day: alert.day,
    /** Alerts still loading — show the list's loading state, not "no match". */
    resolving: active && alert.resolving,
    /** The alert's members to render, or null when the ordinary list applies. */
    rows,
    /** The segment to show for this visit; undefined keeps the saved one. */
    types: active ? [alert.kind] : undefined,
    clear,
  };
}
